import { useRef } from "react";
import { motion, useScroll, useTransform, useReducedMotion } from "framer-motion";
import { Eyebrow, Reveal } from "./bits";

export const About = () => {
  const ref = useRef(null);
  const reduce = useReducedMotion();
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [reduce ? 0 : -36, reduce ? 0 : 36]);

  return (
    <section
      id="about"
      data-testid="about-section"
      ref={ref}
      className="relative border-y border-[#10202E]/[0.06] bg-[#DDF4FC] py-28 md:py-40"
    >
      <div className="mx-auto max-w-6xl px-6">
        <Reveal>
          <Eyebrow>08 — About HRKAPITAL</Eyebrow>
        </Reveal>
        <Reveal delay={0.08}>
          <h2 className="mt-6 max-w-4xl font-display text-4xl leading-[1.1] tracking-tight sm:text-5xl lg:text-6xl">
            Money is personal.{" "}
            <span className="italic text-emerald-600">Your financial strategy should be too.</span>
          </h2>
        </Reveal>

        <div className="mt-16 grid gap-14 lg:grid-cols-[1.1fr_0.9fr] lg:gap-20">
          <div className="space-y-8">
            <Reveal>
              <p className="text-lg font-light leading-relaxed text-[#1C3347]">
                HRKAPITAL was founded on a simple observation: most people don't need more financial
                products — they need fewer, chosen well, held with conviction.
              </p>
            </Reveal>
            <Reveal delay={0.05}>
              <p className="text-base font-light leading-relaxed text-[#33506A]">
                As an AMFI-registered mutual fund distributor, our work begins long before any
                recommendation. We listen first — to your goals, your constraints, your anxieties —
                and only then shape a strategy that fits the life you're actually living. No
                jargon. No pressure. No product-pushing.
              </p>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="text-base font-light leading-relaxed text-[#33506A]">
                Our commitment is long-term by design. We stay with you through reviews, life
                changes and market cycles, because wealth isn't built in quarters — it's built in
                decades, one disciplined decision at a time.
              </p>
            </Reveal>
            <Reveal delay={0.15}>
              <blockquote
                data-testid="about-quote"
                className="border-l-2 border-emerald-600/60 pl-6 pt-1"
              >
                <p className="font-display text-2xl italic leading-snug text-[#10202E] sm:text-3xl">
                  "We don't sell products. We build paths."
                </p>
                <cite className="mt-3 block font-mono text-[11px] uppercase not-italic tracking-[0.25em] text-[#6E8698]">
                  The HRKAPITAL philosophy
                </cite>
              </blockquote>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="relative overflow-hidden rounded-3xl border border-[#10202E]/10">
              <motion.img
                data-testid="about-image"
                style={{ y: imgY }}
                src="/assets/goals/about.png"
                alt="3D clay illustration of an ascending spiral path toward a golden sphere — clarity by design"
                loading="lazy"
                className="h-[420px] w-full scale-[1.12] object-cover lg:h-[520px]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-white/70 via-transparent to-transparent" />
              <span className="absolute bottom-5 left-6 font-mono text-[10px] uppercase tracking-[0.3em] text-emerald-700">
                Clarity, by design
              </span>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
